import { useNavigate } from "react-router-dom";
import i18n from "../../../language/i18n";
import { BaseModal, Card, Icon } from "../../../library";
import { useTour } from "./tourContext";
import { getAllTours } from "./tourRegistry";

interface TourLauncherPanelProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

// Opened by the left-nav footer button ("Nasıl kullanırım?"). A friendly hero plus
// the available guided tours as rich glass cards; picking one closes the panel and
// starts the tour.
const TourLauncherPanel = ({ isOpen, onRequestClose }: TourLauncherPanelProps) => {
  const { startTour } = useTour();
  const navigate = useNavigate();
  const tours = getAllTours();

  const launch = (tour: (typeof tours)[number]) => {
    onRequestClose();
    // Per-screen tours anchor to elements on their own route — navigate there
    // first if we're elsewhere; the overlay retries until the anchors mount.
    if (tour.route && window.location.pathname !== tour.route) {
      navigate(tour.route);
    }
    startTour(tour.id);
  };

  return (
    <BaseModal
      title={i18n.t("how_can_I_use")}
      icon="🧭"
      isOpen={isOpen}
      size="medium"
      onRequestClose={onRequestClose}
    >
      <div className="tlp-root">
        <div className="tlp-hero">
          <div className="tlp-hero-text">
            <div className="tlp-hero-title">
              {i18n.t("tour_launcher_headline")}
            </div>
            <div className="tlp-hero-sub">{i18n.t("tour_launcher_intro")}</div>
          </div>
        </div>

        <div className="tlp-list">
          {tours.map((tour) => (
            <Card
              key={tour.id}
              variant="glass"
              interactive
              className="tlp-card"
              onClick={() => launch(tour)}
            >
              <span className="tlp-card-icon">
                <Icon name={tour.iconName || "path"} size="medium" />
              </span>
              <span className="tlp-card-text">
                <span className="tlp-card-title">{i18n.t(tour.titleKey)}</span>
                {tour.descriptionKey ? (
                  <span className="tlp-card-desc">
                    {i18n.t(tour.descriptionKey)}
                  </span>
                ) : null}
                <span className="tlp-card-meta">
                  <span className="tlp-meta-item">
                    <Icon name="listChecks" size="xsmall" />
                    {tour.steps.length} {i18n.t("tour_steps")}
                  </span>
                  <span className="tlp-meta-dot" />
                  <span className="tlp-meta-item">
                    <Icon name="clock" size="xsmall" />
                    {i18n.t("tour_duration")}
                  </span>
                </span>
              </span>
              <span className="tlp-card-cta">
                <Icon name="play" size="small" />
                <span className="tlp-card-cta-label">{i18n.t("start")}</span>
              </span>
            </Card>
          ))}
        </div>
      </div>
    </BaseModal>
  );
};

export default TourLauncherPanel;
